import { isDictionary } from "@/helpers/is-dictionary";
import { isObject, type ReferenceType } from "@/helpers/is-object";
import { isValidKey } from "@/helpers/is-valid-key";

/**
 * Create a deep clone of any value
 * Preserves prototypes for plain objects and handles:
 * - Primitives
 * - Date objects
 * - RegExp objects
 * - Arrays
 * - Plain objects (preserving prototype)
 * - Cyclic references
 * - Other objects (shallow copy)
 *
 * @param value - The value to clone
 * @param visited - WeakMap tracking already processed objects to handle cyclic references
 * @returns A new cloned value
 */
export function cloneDeep<T>(value: T, visited = new WeakMap<ReferenceType, any>()): T {
  // Handle non-object types
  if (!isObject(value)) return value;

  // Handle cyclic references
  if (visited.has(value)) return visited.get(value);

  // Handle Date objects
  if (value instanceof Date) {
    return new Date(value.getTime()) as T;
  }

  // Handle RegExp objects
  if (value instanceof RegExp) {
    return new RegExp(value.source, value.flags) as T;
  }

  // Handle Arrays
  if (Array.isArray(value)) {
    const clone = [] as any[];

    // Track this array in our visited map before we clone children
    visited.set(value, clone);

    // Clone array items, handling potential cyclic references
    value.forEach((item, index) => {
      clone[index] = cloneDeep(item, visited);
    });

    return clone as T;
  }

  // Handle plain objects
  if (isDictionary(value)) {
    // Create a new object with the same prototype as the original
    const result = Object.create(
      Object.getPrototypeOf(value),
      Object.getOwnPropertyDescriptors(value)
    );

    // Track this object in our visited map before we clone children
    visited.set(value, result);

    // Clone object properties, handling potential cyclic references
    Reflect.ownKeys(result).forEach((key) => {
      if (isValidKey(key) && Reflect.has(result, key)) {
        const clone = cloneDeep(Reflect.get(result, key), visited);
        Reflect.set(result, key, clone);
      }
    });

    return result as T;
  }

  // Handle other objects (shallow copy)
  return value;
}
